import React from 'react';
import { type MarcaRequest, type MarcaFormErrors } from '../types/marca';
import { MarcaValidation } from '../services/marcaValidation';
import { MarcaService } from '../services/marcaService';

interface UseMarcaFormReturn {
  formData: MarcaRequest;
  errors: MarcaFormErrors;
  loading: boolean;
  isValid: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: () => Promise<void>;
  resetForm: () => void;
}

export const useMarcaForm = (
  marcaId: number | null,
  onSave: (marca: MarcaRequest) => void
): UseMarcaFormReturn => {
  const [formData, setFormData] = React.useState<MarcaRequest>({
    descripcion: '',
  });
  const [errors, setErrors] = React.useState<MarcaFormErrors>({});
  const [loading, setLoading] = React.useState(false);

  const isValid = React.useMemo(() => {
    return MarcaValidation.isValidMarca(formData);
  }, [formData]);

  React.useEffect(() => {
    const loadMarca = async () => {
      if (marcaId) {
        setLoading(true);
        try {
          const marca = await MarcaService.getMarcaById(marcaId);
          setFormData({
            idMarca: marca.idMarca,
            descripcion: marca.descripcion,
          });
        } catch (error) {
          console.error('Error loading marca:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadMarca();
  }, [marcaId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear specific error when user starts typing
    if (errors[name as keyof MarcaFormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async () => {
    const validationErrors = MarcaValidation.validateMarca(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setLoading(true);
      try {
        await onSave(formData);
      } catch (error) {
        console.error('Error saving marca:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = React.useCallback(() => {
    setFormData({
      descripcion: '',
    });
    setErrors({});
  }, []);

  return {
    formData,
    errors,
    loading,
    isValid,
    handleChange,
    handleSubmit,
    resetForm,
  };
};